import { useState } from 'react';
import { append, slice, tail, length, dec, inc, prop, equals, adjust } from 'ramda';
import { ACTION_UNDO } from '../_CONST';

const useHistoryStack = ({ onApply, onLayoutSync }) => {
  const HISTORY_STACK_LIMIT = Number(process.env.REACT_APP_HISTORY_STACK_LIMIT) || 50;

  const [historyActions, setHistoryActions] = useState([]);
  const [historyIndex, setHistoryIndex] = useState(-1);
  const [isUndoPreviousEditorState, setIsUndoPreviousEditorState] = useState(false);

  const addActionOnPrevious = (newValue) => setHistoryActions(
    adjust(
      -1,
      (lastAction) => ({ ...lastAction, newValue })
    )
  );

  const addToHistory = (previousValue, newValue, type, isTextUpdate = false) => {
    const slicedHistory = slice(0, historyIndex + 1, historyActions);
    const appendedHistory = append({
      previousValue,
      newValue,
      type,
      isTextUpdate,
      timestamp: Date.now()
    }, slicedHistory);
    const newHistory = length(appendedHistory) > HISTORY_STACK_LIMIT ? tail(appendedHistory) : appendedHistory;
    setHistoryActions(newHistory);
    setHistoryIndex(length(newHistory) - 1);
  };

  const applyHistoryAction = (actionType) => {
    const isUndo = equals(actionType, ACTION_UNDO);
    const targetIndex = isUndo ? historyIndex : historyIndex + 1;

    // Check if the action is out of bounds (no more undo/redo possible)
    const isAtHistoryStackLimit = (isUndo && historyIndex < 0) || (!isUndo && historyIndex >= length(historyActions) - 1);
    if (isAtHistoryStackLimit) return;

    const action = historyActions[targetIndex];
    const type = prop('type', action);
    const value = prop(isUndo ? 'previousValue' : 'newValue', action);
    const isFrameStyle = equals(type, 'updateFrameStyle');

    // Synchronize the layout if the action is not related to frame style
    if (!isFrameStyle) onLayoutSync(value, type, isUndo);

    // Apply the value using the provided callback
    onApply(value);

    // Set the undo state if the action is related to element changes or frame style
    if (equals(type, 'onChangeElement') || isFrameStyle) setIsUndoPreviousEditorState(true);

    // Update the history index based on the action type
    setHistoryIndex(isUndo ? dec : inc);
  };

  return {
    isUndoPreviousEditorState,
    setIsUndoPreviousEditorState,
    historyActions,
    addActionOnPrevious,
    addToHistory,
    applyHistoryAction,
    canUndo: historyIndex >= 0,
    canRedo: historyIndex < length(historyActions) - 1
  };
};

export default useHistoryStack;